import { createSelector } from 'redux-bundler';
import { snakeCase } from 'lodash';

import Template from '../models/template';
import TemplateInstanceTypeAssignment from '../models/template_instance_type_assignment';
import TemplateSecurityGroupAssignment from '../models/template_security_group_assignment';

export default {
  name: 'templates',
  getReducer: () => {
    const initialData = {
      loading: false,
      items: null,
      sortDirection: 'ASC',
      sortBy: 'name',
      activeTemplate: null,
      loadingActiveTemplate: false,
    };

    return (state = initialData, { type, payload }) => {
      if (type === 'FETCH_TEMPLATES') {
        return { ...state, loading: true };
      }
      if (type === 'FETCH_TEMPLATES_SUCCESS') {
        return {
          ...state,
          loading: false,
          items: payload.result,
        };
      }
      if (type === 'CHANGE_TEMPLATE_SORT') {
        return {
          ...state,
          sortDirection: payload.sortDirection,
          sortBy: payload.sortBy,
        };
      }
      if (type === 'FETCH_TEMPLATE_START') {
        return { ...state, loadingActiveTemplate: true };
      }
      if (type === 'FETCH_TEMPLATE_SUCCESS') {
        return {
          ...state,
          loadingActiveTemplate: false,
          activeTemplate: payload.result,
        };
      }
      if (type === 'UPDATE_TEMPLATE') {
        return {
          ...state,
          activeTemplate: payload.template,
        };
      }
      if (type === 'REMOVE_TEMPLATE') {
        return {
          ...state,
          activeTemplate: null,
        };
      }
      if (type === 'RESET_ACTIVE_FACILITY') {
        return {
          ...state,
          items: null,
        };
      }
      if (type === 'RESET_ACTIVE_TEMPLATE') {
        return {
          ...state,
          activeTemplate: null,
        };
      }

      return state;
    };
  },

  doFetchTemplates: () => async ({ dispatch, getState, store }) => {
    const state = getState();

    const { sortBy } = state.templates;
    const { sortDirection } = state.templates;

    const facility = store.selectActiveFacility(state);
    dispatch({ type: 'FETCH_TEMPLATES' });
    const scope = Template.where({
      kept: true,
      facility_id: facility.id,
    })
      .order({
        [snakeCase(sortBy)]: sortDirection.toLowerCase(),
      })
      .includes([
        'image_pipeline',
        'image',
        'instance_type',
        '',
        { image_pipeline: ['last_image'] },
        { template_instance_type_assignments: 'instance_type' },
      ]);

    const response = await scope.all();

    dispatch({
      type: 'FETCH_TEMPLATES_SUCCESS',
      payload: { result: response.data },
    });
  },

  doFetchTemplate: templateId => async ({ dispatch }) => {
    dispatch({ type: 'FETCH_TEMPLATE_START' });

    const response = await Template.includes([
      'instance_type',
      'image',
      { image_pipeline: ['last_image', { facility: 'region' }] },
      'subnet',
      'keypair',
      'availability_zone',
      'vpc',
      'security_groups',
      { template_security_group_assignments: 'security_group' },
      { template_instance_type_assignments: 'instance_type' },
      'chef_package',
    ]).find(templateId);
    dispatch({
      type: 'FETCH_TEMPLATE_SUCCESS',
      payload: { result: response.data },
    });
  },

  doChangeTemplatesSort: ({ sortDirection, sortBy }) => async ({
    dispatch,
    store,
  }) => {
    dispatch({
      type: 'CHANGE_TEMPLATE_SORT',
      payload: { sortDirection, sortBy },
    });
    store.doFetchTemplates();
  },

  doDuplicateTemplate: (template, templateName, shortName) => async ({
    dispatch,
    getState,
    store,
  }) => {
    const state = getState();
    const facility = store.selectActiveFacility(state).dup();

    const attrs = Object.assign(
      {},
      template.attributes,
      template.relationships,
    );

    // strip createdAt
    delete attrs.createdAt;
    delete attrs.updatedAt;
    delete attrs.lastImageName;
    delete attrs.lastImageIdentifier;
    const newTemplate = new Template(attrs);

    newTemplate.name = templateName;
    newTemplate.shortName = shortName;
    newTemplate.facility = facility;
    newTemplate.templateSecurityGroupAssignments = template.templateSecurityGroupAssignments.map(
      tsga => {
        const sg = tsga.securityGroup;
        const newTsga = new TemplateSecurityGroupAssignment({
          securityGroup: sg,
        });
        return newTsga;
      },
    );
    const orderedInstanceTypes = template.orderedInstanceTypes();

    // A single instance type needs no fallback list — copy assignments only for
    // multi-instance fallback (more than one type); otherwise persist via the
    // singular instance_type only. See doCreateTemplate.
    newTemplate.templateInstanceTypeAssignments =
      orderedInstanceTypes.length > 1
        ? (template.templateInstanceTypeAssignments || [])
            .slice()
            .sort((a, b) => a.position - b.position)
            .map(
              (titas, position) =>
                new TemplateInstanceTypeAssignment({
                  instanceType: titas.instanceType,
                  position,
                }),
            )
        : [];

    // Backend requires the singular instance_type; set it to the primary
    // (first ordered) instance type.
    const [primaryInstanceType] = orderedInstanceTypes;
    newTemplate.instanceType = primaryInstanceType;

    await newTemplate.save({
      with: [
        'facility.id',
        'image.id',
        'imagePipeline.id',
        'instanceType.id',
        'keypair.id',
        'vpc.id',
        'availabilityZone.id',
        'subnet.id',
        { templateSecurityGroupAssignments: 'securityGroup.id' },
        { templateInstanceTypeAssignments: 'instanceType.id' },
      ],
    });

    if (template.isPersisted) {
      facility.templates.push(template);

      await store.doFetchTemplates();
    }

    return template;
  },

  doCreateTemplate: values => async ({ dispatch, getState, store }) => {
    const state = getState();
    const facility = store.selectActiveFacility(state).dup();
    const templateSecurityGroupAssignments = values.securityGroups.map(
      sg => new TemplateSecurityGroupAssignment({ securityGroup: sg }),
    );
    const instanceTypeList = values.instanceTypes || [];

    const { instanceTypes: _instanceTypes, ...templateValues } = values;

    // The backend Template requires a singular instance_type; it can't be
    // derived from the nested assignments during a create (the parent is
    // validated before its assignment rows exist). Set the primary (first)
    // instance type explicitly so the template passes validation.
    const [primaryInstanceType] = instanceTypeList;

    // A single instance type needs no fallback list — persist it via the
    // singular instance_type only. The ordered assignments are reserved for
    // multi-instance fallback (more than one type).
    const templateInstanceTypeAssignments =
      instanceTypeList.length > 1
        ? instanceTypeList.map(
            (instanceType, position) =>
              new TemplateInstanceTypeAssignment({ instanceType, position }),
          )
        : [];

    const template = new Template({
      facility,
      ...templateValues,
      instanceType: primaryInstanceType,
      templateSecurityGroupAssignments,
      templateInstanceTypeAssignments,
    });

    await template.save({
      with: [
        'facility.id',
        'image.id',
        'imagePipeline.id',
        'instanceType.id',
        'keypair.id',
        'vpc.id',
        'availabilityZone.id',
        'subnet.id',
        'chefPackage.id',
        { templateSecurityGroupAssignments: 'securityGroup.id' },
        { templateInstanceTypeAssignments: 'instanceType.id' },
      ],
    });

    if (template.isPersisted) {
      facility.templates.push(template);

      await store.doFetchTemplates();
    }

    return template;
  },

  doUpdateTemplate: values => async ({ dispatch, getState, store }) => {
    const state = getState();
    const facility = store.selectActiveFacility(state).dup();
    const template = store.selectActiveTemplate(state).dup();
    const tsgasToDestroy = template.templateSecurityGroupAssignments
      .filter(
        ({ securityGroup: { id } }) =>
          !values.securityGroups.map(sg => sg.id).includes(id),
      )
      .map(tsga => {
        const nextTsga = tsga;
        nextTsga.isMarkedForDestruction = true;
        return nextTsga;
      });
    const tsgasToCreate = values.securityGroups
      .filter(
        ({ id }) =>
          !template.templateSecurityGroupAssignments
            .map(({ securityGroup: sg }) => sg.id)
            .includes(id),
      )
      .map(sg => new TemplateSecurityGroupAssignment({ securityGroup: sg }));

    const titasToDestroy = (
      template.templateInstanceTypeAssignments || []
    ).map(titas => {
      const next = titas;
      next.isMarkedForDestruction = true;
      return next;
    });
    // A single instance type needs no fallback list — destroy any existing
    // assignments (above) and recreate none, persisting via the singular
    // instance_type only. Assignments are recreated only for multi-instance
    // fallback (more than one type).
    const titasToCreate =
      (values.instanceTypes || []).length > 1
        ? (values.instanceTypes || []).map(
            (instanceType, position) =>
              new TemplateInstanceTypeAssignment({ instanceType, position }),
          )
        : [];

    const { image, imagePipeline, instanceTypes: _instanceTypes, ...otherValues } = values;

    if (image === null && template.image) {
      template.image.isMarkedForDisassociation = true;
    } else {
      template.image = image;
    }

    if (imagePipeline === null && template.imagePipeline) {
      template.imagePipeline.isMarkedForDisassociation = true;
    } else {
      template.imagePipeline = imagePipeline;
    }

    // Keep the singular instance_type (required by the backend) in sync with
    // the primary (first) entry of the ordered assignment list.
    const [primaryInstanceType] = values.instanceTypes || [];

    template.assignAttributes({
      ...otherValues,
      instanceType: primaryInstanceType,
      templateSecurityGroupAssignments: [...tsgasToDestroy, ...tsgasToCreate],
      templateInstanceTypeAssignments: [...titasToDestroy, ...titasToCreate],
    });

    await template.save({
      with: [
        'facility.id',
        'image.id',
        'imagePipeline.id',
        'instanceType.id',
        'keypair.id',
        'vpc.id',
        'availabilityZone.id',
        'subnet.id',

        'chefPackage.id',
        { templateSecurityGroupAssignments: 'securityGroup.id' },
        { templateInstanceTypeAssignments: 'instanceType.id' },
      ],
    });

    if (template.isPersisted) {
      await store.doFetchTemplate(template.id);
      await store.doFetchFacility(facility.id);
    }

    return template;
  },

  doDestroyTemplate: template => async ({ dispatch, store, getState }) => {
    const state = getState();
    const nextTemplate = template.dup();
    await nextTemplate.destroy();
    dispatch({ type: 'REMOVE_TEMPLATE', payload: { template: nextTemplate } });
    await store.doFetchTemplates();
  },

  doClearActiveTemplate: () => async ({ dispatch, store, getState }) => {
    dispatch({ type: 'REMOVE_TEMPLATE' });
  },

  doResetActiveTemplate: () => async ({ dispatch }) => {
    dispatch({ type: 'RESET_ACTIVE_TEMPLATE' });
  },

  selectTemplateState: state => state.templates,
  selectActiveTemplate: createSelector(
    'selectTemplateState',
    templateState => templateState.activeTemplate,
  ),

  selectTemplateId: createSelector(
    'selectQueryObject',
    queryObject => queryObject.templateId,
  ),

  reactShouldFetchActiveTemplate: createSelector(
    'selectRouteParams',
    'selectPathname',
    'selectActiveTemplate',
    'selectTemplateState',
    (routeParams, pathname, activeTemplate, templateState) => {
      if (
        !pathname.includes('/templates') ||
        !routeParams.templateId ||
        templateState.loadingActiveTemplate
      ) {
        return null;
      }
      if (activeTemplate && activeTemplate.id === routeParams.templateId) {
        return null;
      }

      return {
        actionCreator: 'doFetchTemplate',
        args: [routeParams.templateId],
      };
    },
  ),

  reactShouldFetchTemplates: createSelector(
    'selectRouteApis',
    'selectTemplatesRaw',
    'selectCurrentUser',
    'selectActiveClient',
    'selectActiveFacility',
    (apis, templatesRaw, currentUser, activeClient, activeFacility) => {
      const wantsTemplates = apis.includes('templates');
      if (
        !wantsTemplates ||
        templatesRaw.loading ||
        templatesRaw.items ||
        !currentUser ||
        !activeClient ||
        !activeFacility
      ) {
        return false;
      }
      return { actionCreator: 'doFetchTemplates' };
    },
  ),
  reactShouldResetActiveTempate: createSelector(
    'selectActiveTemplate',
    'selectRouteParams',
    (activeTemplate, routeParams) => {
      if (!activeTemplate) {
        return false;
      }
      if (routeParams.templateId) {
        return false;
      }
      return { actionCreator: 'doResetActiveTemplate' };
    },
  ),

  selectTemplatesRaw: state => state.templates,
  selectTemplates: state => state.templates.items || [],
  selectTemplatesLoading: state => state.templates.loading,

  selectTemplatesSortBy: state => state.templates.sortBy,
  selectTemplatesSortDirection: state => state.templates.sortDirection,
};
